import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setFieldValue from "@insite/client-framework/Store/Components/ContactUsForm/Handlers/SetFieldValue";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ContactFormContext } from "@insite/content-library/Widgets/ContactUs/ContactForm";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import { CONTACT_US_FORM_FIELDS } from "@thkStore/Constants/ContactUs/formFields";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    label = "label",
    hintText = "hintText",
    isRequired = "isRequired",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.label]: string;
        [fields.hintText]: string;
        [fields.isRequired]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    stateValue: state.components.contactUsForm.fieldValues[CONTACT_US_FORM_FIELDS.state],
    selectedCountryAbbr: state.components.contactUsForm.fieldValues[CONTACT_US_FORM_FIELDS.country],
    countries: getCurrentCountries(state),
});

const mapDispatchToProps = { setFieldValue };

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface StateStyles {
    stateSelect?: SelectPresentationProps;
    stateTextField?: TextFieldPresentationProps;
}

export const stateStyles: StateStyles = {};

const styles = stateStyles;

const State: React.FC<Props> = ({
    fields, stateValue, selectedCountryAbbr, countries, setFieldValue,
}) => {
    const { label, hintText, isRequired } = fields;
    const contactFormContext = React.useContext(ContactFormContext);
    const [errorMessage, setErrorMessage] = React.useState("");

    React.useEffect(() => {
        contactFormContext.validators.state = validate;
        if (typeof stateValue !== "undefined") validate();
    }, [stateValue, isRequired]);

    const validate = () => {
        const error = isRequired && !stateValue
            ? (siteMessage("Field_Required", label) as string)
            : "";
        setErrorMessage(error);
        return !error;
    };

    const availableStates =
        (countries ?? []).find(c => c.abbreviation === selectedCountryAbbr)?.states ?? [];

    const handleChange = (event: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) =>
        setFieldValue({ key: CONTACT_US_FORM_FIELDS.state, value: event.target.value });

    // US/CA/MX always carry states; the TextField is a safety net for a country without them.
    if (availableStates.length === 0) {
        return (
            <TextField
                {...styles.stateTextField}
                label={label}
                required={isRequired}
                placeholder={hintText}
                value={stateValue || ""}
                error={errorMessage}
                onChange={handleChange}
            />
        );
    }

    return (
        <Select
            {...styles.stateSelect}
            label={label}
            required={isRequired}
            value={stateValue || ""}
            error={errorMessage}
            onChange={handleChange}
        >
            <option value="">{translate("Select State")}</option>
            {availableStates.map(s => (
                <option key={s.id} value={s.name}>
                    {s.name}
                </option>
            ))}
        </Select>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(State),
    definition: {
        group: "Contact Us",
        icon: "input-pipe",
        fieldDefinitions: [
            { name: fields.label, displayName: "Label", editorTemplate: "TextField", fieldType: "Translatable", defaultValue: "State", isRequired: true },
            { name: fields.hintText, displayName: "Hint Text", editorTemplate: "TextField", fieldType: "Translatable", defaultValue: "Enter State", isRequired: true },
            { name: fields.isRequired, displayName: "Required Field", editorTemplate: "CheckboxField", defaultValue: true, fieldType: "General" },
        ],
    },
};

export default widgetModule;
